import { useBookingV2 } from "context/BookingV2Context"
import SelectionCard from "../SelectionCard"
import { useMemo, useEffect } from "react"
import dayjs from "dayjs"
import { ItimeSlot } from "types/ticket"
import { useCartDispatch, useCartState } from "hooks/useCartV2"
import { ImprovedCartActionsEnum } from "types/improved-booking"
import { isTimeslotDisabled } from "utils/helpers/bookingv2/ticket"

const SelectTime = () => {
    const { tickets, event, selectedDateKey } = useBookingV2()
    const cartDispatch = useCartDispatch()
    const cartState = useCartState()

    const daySlots = useMemo(() => {
        if (!selectedDateKey) return []
        return tickets.filter(
            (s) => dayjs(s.start_slot).format("YYYY-MM-DD") === selectedDateKey
        )
    }, [tickets, selectedDateKey])

    const handleSelectTime = (slot: ItimeSlot) => {
        if (isTimeslotDisabled(slot)) return

        const currentDate = dayjs(slot.start_slot)
        const enriched = {
            timeslot_id: slot._id,
            dayName: currentDate.format("dddd"),
            dayNumber: currentDate.date(),
            monthName: currentDate.format("MMMM"),
            monthNum: currentDate.month() + 1,
            yearNum: currentDate.year().toString(),
            start: new Date(slot.start_slot),
            end: new Date(slot.end_slot),
            types: slot.types.map((type) => {
                const id =
                    typeof type.type_id === "string"
                        ? type.type_id
                        : //@ts-ignore
                          type.type_id?._id || ""

                const ticketDetails = event.availableTickets.find(
                    (t) => t._id === id
                )
                return {
                    _id: id,
                    type_id: id,
                    // Display data from availableTickets
                    ar_description: ticketDetails?.ar_description ?? "",
                    en_description: ticketDetails?.en_description ?? "",
                    en_title: ticketDetails?.en_title ?? "",
                    ar_title: ticketDetails?.ar_title ?? "",
                    color: ticketDetails?.color ?? "#ffffff",
                    discount: ticketDetails?.discount ?? {
                        current_value: 0,
                        previous_value: 0,
                    },
                    // Quantities from time_slots.types
                    limit: type.limit,
                    // Min/max from availableTickets
                    max: ticketDetails?.max ?? 0,
                    min: ticketDetails?.min ?? 1,
                    must_select_seat: ticketDetails?.must_select_seat ?? false,
                    price: ticketDetails?.price ?? 0,
                    group: (ticketDetails as any)?.group,
                    wait_list: type.wait_list,
                }
            }),
            seats_season_id: event.seatsio?.season_id || "",
            allow_using_coupon: slot.allow_using_coupon,
        }

        // Replace any existing placeholder with actual selection
        cartDispatch({
            type: ImprovedCartActionsEnum.REMOVE_DAY,
        })

        cartDispatch({
            type: ImprovedCartActionsEnum.ADD_DAY,
            payload: enriched,
        })
    }

    // Check which timeslot is currently selected
    const selectedTimeslotId = cartState.selectedDays?.timeslot_id

    // If there is exactly one enabled timeslot, auto-select it but DO NOT auto-advance.
    useEffect(() => {
        if (!daySlots || daySlots.length === 0) return
        const enabledSlots = daySlots.filter((s) => !isTimeslotDisabled(s))
        if (enabledSlots.length !== 1) return
        const onlySlot = enabledSlots[0]

        // If not yet selected, select it automatically for convenience
        if (selectedTimeslotId !== onlySlot._id) {
            handleSelectTime(onlySlot)
        }
    }, [daySlots, selectedTimeslotId])

    return (
        <div className="w-full">
            <div className="w-full flex flex-col gap-y-1">
                {daySlots.map((slot, index) => {
                    const disabled = isTimeslotDisabled(slot)
                    const isSelected = selectedTimeslotId === slot._id

                    return (
                        <SelectionCard
                            variant="time"
                            key={slot._id}
                            startTime={slot.start_slot}
                            endTime={slot.end_slot}
                            isFirst={index === 0}
                            isLast={index === daySlots.length - 1}
                            isDisabled={disabled}
                            isSelected={isSelected}
                            onClick={() => handleSelectTime(slot)}
                        />
                    )
                })}
            </div>
        </div>
    )
}

export default SelectTime
